import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LegalPage from "./LegalPage";
import api from "@/lib/api";

const BRAND = "Manthan Foundation";
const DOMAIN = "manthanfoundation.in";

const DEFAULT_SECTIONS = [
  {
    id: "introduction",
    title: "1. Introduction",
    content: (
      <>
        <p>Welcome to <strong>{BRAND}</strong> ("we," "us," or "our"). These Terms and Conditions ("Terms") govern your access to and use of our website located at <strong>{DOMAIN}</strong> (the "Website"), our teacher training programs, certification services, podcast platform, and all related services (collectively, the "Services").</p>
        <p>By accessing the Website or using any of our Services, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not agree with any part of these Terms, please discontinue use of the Website immediately.</p>
      </>
    ),
  },
  {
    id: "acceptance",
    title: "2. Acceptance of Terms",
    content: (
      <>
        <p>By registering for a training program, creating an account, submitting a form, or browsing the Website, you acknowledge that:</p>
        <ul>
          <li>You have read and accept these Terms in full.</li>
          <li>You have the legal capacity to enter into a binding agreement.</li>
          <li>You will comply with all applicable laws and regulations of India.</li>
          <li>These Terms constitute a legally binding agreement between you and {BRAND}.</li>
        </ul>
        <p>We reserve the right to amend these Terms at any time. Continued use of the Website following any amendment constitutes your acceptance of the revised Terms.</p>
      </>
    ),
  },
  {
    id: "about",
    title: "3. About Manthan Foundation",
    content: (
      <>
        <p><strong>{BRAND}</strong> is an educational organization dedicated to advancing teacher professional development, training, and capacity building across schools in India. Our services include:</p>
        <ul>
          <li>Certified teacher training programs.</li>
          <li>QR-verified digital certificates of completion.</li>
          <li>Educational podcast programs featuring practising educators.</li>
          <li>School registration and training booking services.</li>
          <li>Resource sharing and community engagement for educators.</li>
        </ul>
        <p>We operate as an independent educational initiative and are not affiliated with any government body unless explicitly stated in program documentation.</p>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "4. Eligibility",
    content: (
      <>
        <p>Our Services are intended for:</p>
        <ul>
          <li>Practising teachers, principals, academic coordinators, and school leadership.</li>
          <li>Educational institutions, schools, and associated administrative staff.</li>
          <li>Individuals with a genuine interest in teacher professional development.</li>
        </ul>
        <p>By using our Services, you represent that you are at least 18 years of age. If you are registering on behalf of a school or institution, you confirm that you have the authority to do so.</p>
        <p>We reserve the right to refuse or revoke access to our Services to any individual or institution at our discretion.</p>
      </>
    ),
  },
  {
    id: "user-accounts",
    title: "5. User Accounts",
    content: (
      <>
        <p>Certain features of our Website require an account. Accounts are created and managed by administrators of {BRAND}. Self-registration is not available to the general public.</p>
        <ul>
          <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
          <li>You must notify us immediately of any unauthorized use of your account.</li>
          <li>You may not share your login credentials with any third party.</li>
          <li>You are responsible for all activity that occurs under your account.</li>
          <li>{BRAND} reserves the right to suspend or terminate accounts that violate these Terms.</li>
        </ul>
      </>
    ),
  },
  {
    id: "training-registration",
    title: "6. Training Program Registration",
    content: (
      <>
        <p>By registering for a training program through our Website:</p>
        <ul>
          <li>You confirm that all information provided during registration is accurate and complete.</li>
          <li>You understand that registration is subject to availability and approval.</li>
          <li>You acknowledge that {BRAND} may cancel or reschedule programs due to unforeseen circumstances, with reasonable notice provided.</li>
          <li>You agree to adhere to the program schedule, attendance requirements, and conduct standards communicated at the time of registration.</li>
          <li>Incomplete participation may affect eligibility for certificates.</li>
        </ul>
      </>
    ),
  },
  {
    id: "certificates",
    title: "7. Certificates",
    content: (
      <>
        <p>Certificates issued by {BRAND} are digital documents that verify participation in and completion of our training programs. By receiving a certificate, you agree that:</p>
        <ul>
          <li>Certificates are issued solely on the basis of genuine program participation and completion.</li>
          <li>Each certificate contains a unique QR code that can be used to verify its authenticity.</li>
          <li>Certificates may not be altered, forged, or misrepresented.</li>
          <li>Certificates do not constitute formal academic qualifications unless otherwise stated.</li>
          <li>{BRAND} reserves the right to revoke a certificate if it is determined that it was issued based on false information or fraudulent participation.</li>
        </ul>
        <div className="highlight-box">
          <strong>Disclaimer:</strong> Completion of a training program and receipt of a certificate does not guarantee employment, promotion, accreditation, or any other professional benefit. Certificates are acknowledgements of participation only.
        </div>
      </>
    ),
  },
  {
    id: "bookings",
    title: "8. Online Bookings",
    content: (
      <>
        <p>When booking a training program or session online:</p>
        <ul>
          <li>Bookings are provisional until confirmed by {BRAND}.</li>
          <li>A confirmation message or email will be sent upon successful booking.</li>
          <li>You are responsible for attending the booked session at the confirmed time.</li>
          <li>Requests for rescheduling must be submitted at least 48 hours in advance.</li>
          <li>{BRAND} reserves the right to cancel or reschedule bookings and will make reasonable efforts to notify participants promptly.</li>
        </ul>
      </>
    ),
  },
  {
    id: "payment-refund",
    title: "9. Payment and Refund Policy",
    content: (
      <>
        <p>Where training programs or services carry a fee, the following conditions apply:</p>
        <ul>
          <li>All fees are stated in Indian Rupees (INR) and are inclusive of applicable taxes unless stated otherwise.</li>
          <li>Payments made for programs are generally non-refundable once the program has commenced.</li>
          <li>Cancellations made more than 7 days before program commencement may be eligible for a full or partial refund, at the discretion of {BRAND}.</li>
          <li>In the event of program cancellation by {BRAND}, participants will receive a full refund or may opt to transfer to an alternative program.</li>
          <li>Refund requests must be submitted in writing to our contact email address.</li>
        </ul>
        <p>Where programs are offered free of charge, no financial refunds are applicable.</p>
      </>
    ),
  },
  {
    id: "user-responsibilities",
    title: "10. User Responsibilities",
    content: (
      <>
        <p>By using our Services, you agree to:</p>
        <ul>
          <li>Provide accurate, complete, and current information in all forms and registrations.</li>
          <li>Respect the intellectual property rights of {BRAND} and other participants.</li>
          <li>Maintain professional and respectful conduct in all interactions with our staff, facilitators, and other participants.</li>
          <li>Not engage in any conduct that disrupts, damages, or impairs the Services.</li>
          <li>Comply with all applicable Indian laws and regulations.</li>
          <li>Not use the Website for any unlawful, harmful, or fraudulent purpose.</li>
        </ul>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "11. Intellectual Property",
    content: (
      <>
        <p>All content on this Website, including but not limited to text, graphics, logos, images, audio, video, training materials, certificates, and software, is the property of {BRAND} or its content suppliers and is protected under applicable Indian and international intellectual property laws.</p>
        <ul>
          <li>You may not reproduce, distribute, modify, or create derivative works of our content without prior written permission.</li>
          <li>Training materials provided to participants are for personal, non-commercial use only.</li>
          <li>Podcast content featuring educators is produced and owned by {BRAND}. Participants grant {BRAND} the right to use their recorded contributions for educational and promotional purposes (see Podcast Terms).</li>
        </ul>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "12. Acceptable Use Policy",
    content: (
      <>
        <p>You agree not to use the Website or Services to:</p>
        <ul>
          <li>Upload, post, or transmit any content that is unlawful, defamatory, obscene, or harmful.</li>
          <li>Impersonate any person or entity or misrepresent your affiliation.</li>
          <li>Attempt to gain unauthorized access to any part of the Website or its systems.</li>
          <li>Use automated tools, bots, or scripts to scrape, crawl, or collect data from the Website.</li>
          <li>Transmit spam, unsolicited communications, or malicious software.</li>
          <li>Infringe upon the privacy or intellectual property rights of any third party.</li>
        </ul>
        <p>Violation of this policy may result in immediate suspension of access and, where appropriate, legal action.</p>
      </>
    ),
  },
  {
    id: "limitation-liability",
    title: "13. Limitation of Liability",
    content: (
      <>
        <p>To the fullest extent permitted by applicable law, {BRAND}, its directors, employees, partners, agents, and affiliates shall not be liable for:</p>
        <ul>
          <li>Any indirect, incidental, special, consequential, or punitive damages arising from your use of the Services.</li>
          <li>Loss of data, revenue, or professional opportunity.</li>
          <li>Any interruption, error, or unavailability of the Website.</li>
          <li>Any action taken in reliance on content published on the Website.</li>
        </ul>
        <p>Our total liability in connection with any claim arising from your use of the Services shall not exceed the amount paid by you to {BRAND} in the six months preceding the claim, or ₹1,000, whichever is lower.</p>
      </>
    ),
  },
  {
    id: "third-party-links",
    title: "14. Third-Party Links",
    content: (
      <>
        <p>The Website may contain links to third-party websites. These links are provided for your convenience only. {BRAND} does not endorse, control, or take responsibility for the content, privacy practices, or services offered by third-party websites. We encourage you to review the terms and privacy policies of any third-party sites you visit.</p>
      </>
    ),
  },
  {
    id: "privacy-reference",
    title: "15. Privacy",
    content: (
      <>
        <p>Your use of the Services is also governed by our <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms by reference. By using our Services, you consent to the collection and use of your information as described in the Privacy Policy.</p>
      </>
    ),
  },
  {
    id: "suspension-termination",
    title: "16. Suspension or Termination of Accounts",
    content: (
      <>
        <p>{BRAND} reserves the right, at its sole discretion, to suspend or permanently terminate your access to the Services, with or without notice, for any of the following reasons:</p>
        <ul>
          <li>Violation of these Terms or any applicable law.</li>
          <li>Provision of false or misleading information.</li>
          <li>Conduct deemed harmful to other participants, staff, or the reputation of {BRAND}.</li>
          <li>Non-attendance or abandonment of a booked program without notice.</li>
        </ul>
        <p>Upon termination, your right to use the Services ceases immediately. Any certificates issued prior to termination shall remain valid unless they were obtained through fraudulent means.</p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "17. Governing Law and Jurisdiction",
    content: (
      <>
        <p>These Terms shall be governed by and construed in accordance with the laws of India. Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of India.</p>
        <p>We encourage users to resolve disputes through direct communication with us before initiating formal legal proceedings.</p>
      </>
    ),
  },
  {
    id: "changes-to-terms",
    title: "18. Changes to These Terms",
    content: (
      <>
        <p>{BRAND} reserves the right to update or modify these Terms at any time. When we make material changes, we will update the "Last Updated" date at the top of this page. We encourage you to review these Terms periodically. Your continued use of the Website after any changes constitute your acceptance of the updated Terms.</p>
      </>
    ),
  },
  {
    id: "contact",
    title: "19. Contact Information",
    content: (
      <>
        <p>If you have any questions about these Terms and Conditions, please contact us:</p>
        <div className="highlight-box">
          <p><strong>{BRAND}</strong></p>
          <p>Email: <a href="mailto:info@manthanfoundation.in">info@manthanfoundation.in</a></p>
          <p>Website: <Link to="/contact">Contact Page</Link></p>
        </div>
        <p>We aim to respond to all queries within 5 working days.</p>
      </>
    ),
  },
];

export default function Terms() {
  const [customContent, setCustomContent] = useState(null);

  useEffect(() => {
    api.get("/cms/legal").then((r) => {
      if (r.data?.terms_custom) setCustomContent(r.data.terms_custom);
    }).catch(() => {});
  }, []);

  if (customContent) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-[#0F172A] text-white">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold">Terms &amp; Conditions</h1>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-gray-600 text-[15px] leading-relaxed legal-content"
          dangerouslySetInnerHTML={{ __html: customContent }} />
        <style>{`.legal-content p{margin-bottom:.75rem}.legal-content ul{list-style:disc;padding-left:1.5rem;margin:.75rem 0}.legal-content h2{font-size:1.15rem;font-weight:600;color:#1e293b;margin:1.5rem 0 .5rem}`}</style>
      </div>
    );
  }

  return (
    <LegalPage
      title="Terms & Conditions"
      subtitle="Please read these terms carefully before using our website and services. By accessing Manthan Foundation's platform, you agree to be bound by these terms."
      lastUpdated="1 July 2025"
      breadcrumb="Terms & Conditions"
      sections={DEFAULT_SECTIONS}
    />
  );
}
